/**
 * Consultant Profile Store Slice
 *
 * Zustand slice for consultant profile state management.
 * Domain: ServicesContext > ConsultantProfileAggregate
 *
 * Manages the current consultant's own profile (self-service).
 * Integrates with consultantProfilesApi for fetch and update.
 */

import {
  clearError as clearOpError,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { consultantProfilesApi } from '@/api/consultantProfiles';
import type {
  ConsultantProfile,
  UpdateConsultantProfileRequest,
} from '@/types/consultant-profile.types';

import type { ErrorStates, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ConsultantProfileSlice {
  consultantProfile: ConsultantProfile | null;
  // Loading/error
  consultantProfileIsLoading: LoadingStates;
  consultantProfileHasLoaded: LoadingStates;
  consultantProfileLastLoaded: LoadedTimestamps;
  consultantProfileErrors: ErrorStates;
  // Actions
  fetchConsultantProfile: (userId: string) => Promise<void>;
  updateConsultantProfile: (data: UpdateConsultantProfileRequest) => Promise<void>;
  clearConsultantProfileError: (operation?: string) => void;
  resetConsultantProfile: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialConsultantProfileState = {
  consultantProfile: null,
  consultantProfileIsLoading: {},
  consultantProfileHasLoaded: {},
  consultantProfileLastLoaded: {},
  consultantProfileErrors: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createConsultantProfileSlice: StateCreator<
  ConsultantProfileSlice,
  [],
  [],
  ConsultantProfileSlice
> = (set, get) => ({
  ...initialConsultantProfileState,

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetch a consultant's profile by user ID
   */
  fetchConsultantProfile: async (userId: string) => {
    const operation = 'fetchConsultantProfile';
    const currentState = get();

    if (isOperationLoading(currentState.consultantProfileIsLoading, operation)) return;

    set({
      consultantProfileIsLoading: setLoading(
        currentState.consultantProfileIsLoading,
        operation,
        true
      ),
      consultantProfileErrors: clearOpError(currentState.consultantProfileErrors, operation),
    });

    try {
      const profile = await consultantProfilesApi.getProfile(userId);

      const state = get();
      const loadedState = setLoaded(
        state.consultantProfileHasLoaded,
        state.consultantProfileLastLoaded,
        operation,
        true
      );

      set({
        consultantProfile: profile,
        consultantProfileIsLoading: setLoading(state.consultantProfileIsLoading, operation, false),
        consultantProfileHasLoaded: loadedState.hasLoaded,
        consultantProfileLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(
        state.consultantProfileHasLoaded,
        state.consultantProfileLastLoaded,
        operation,
        false
      );

      set({
        consultantProfileIsLoading: setLoading(state.consultantProfileIsLoading, operation, false),
        consultantProfileErrors: setError(
          state.consultantProfileErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch consultant profile'
        ),
        consultantProfileHasLoaded: loadedState.hasLoaded,
        consultantProfileLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * Update the authenticated consultant's own profile
   */
  updateConsultantProfile: async (data: UpdateConsultantProfileRequest) => {
    const operation = 'updateConsultantProfile';
    const currentState = get();

    if (isOperationLoading(currentState.consultantProfileIsLoading, operation)) return;

    set({
      consultantProfileIsLoading: setLoading(
        currentState.consultantProfileIsLoading,
        operation,
        true
      ),
      consultantProfileErrors: clearOpError(currentState.consultantProfileErrors, operation),
    });

    try {
      const profile = await consultantProfilesApi.updateProfile(data);

      const state = get();
      const loadedState = setLoaded(
        state.consultantProfileHasLoaded,
        state.consultantProfileLastLoaded,
        operation,
        true
      );

      set({
        consultantProfile: profile,
        consultantProfileIsLoading: setLoading(state.consultantProfileIsLoading, operation, false),
        consultantProfileHasLoaded: loadedState.hasLoaded,
        consultantProfileLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(
        state.consultantProfileHasLoaded,
        state.consultantProfileLastLoaded,
        operation,
        false
      );

      set({
        consultantProfileIsLoading: setLoading(state.consultantProfileIsLoading, operation, false),
        consultantProfileErrors: setError(
          state.consultantProfileErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to update consultant profile'
        ),
        consultantProfileHasLoaded: loadedState.hasLoaded,
        consultantProfileLastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  /**
   * Clear error for a specific operation or all errors
   */
  clearConsultantProfileError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({
        consultantProfileErrors: clearOpError(currentState.consultantProfileErrors, operation),
      });
    } else {
      set({ consultantProfileErrors: {} });
    }
  },

  /**
   * Reset consultant profile state to initial values
   */
  resetConsultantProfile: () => {
    set(initialConsultantProfileState);
  },
});
